import React, { useState, useEffect } from 'react';
import { Search, Filter } from 'lucide-react';
import PublicNavbar from '../../components/public/navbar/PublicNavbar';
import PublicFooter from '../../components/public/footer/PublicFooter';
import LocationCard from '../../components/public/cards/LocationCard';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import SEO from '../../components/shared/SEO';
import { getAllLocations } from '../../services/locations';
import './Locations.css';

const Locations = () => {
  const [locations, setLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');

  useEffect(() => {
    loadLocations();
  }, []);

  useEffect(() => {
    filterAndSortLocations();
  }, [searchTerm, sortBy, locations]);

  const loadLocations = async () => {
    setLoading(true);
    const result = await getAllLocations();
    if (result.success) {
      setLocations(result.data);
      setFilteredLocations(result.data);
    }
    setLoading(false);
  };

  const filterAndSortLocations = () => {
    let filtered = [...locations];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(location =>
        location.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        location.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
        location.state.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'price-low':
          return parseFloat(a.price) - parseFloat(b.price);
        case 'price-high':
          return parseFloat(b.price) - parseFloat(a.price);
        default:
          return 0;
      }
    });

    setFilteredLocations(filtered);
  };

  return (
    <div className="public-locations-page">
      <SEO
        title="Filming Locations"
        description="Rent exclusive filming locations in Chicago. Studios, rooftops, warehouses, and unique spaces for film, photo, and commercial productions."
        keywords="filming locations Chicago, studio rental, rooftop filming, warehouse location, production space rental"
        url="/locations"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Locations', url: '/locations' }
        ]}
        schema={{
          '@type': 'Service',
          'name': 'Filming Location Rentals',
          'provider': {
            '@type': 'LocalBusiness',
            'name': 'IZZISHOWIN'
          },
          'serviceType': 'Filming Location Rental',
          'areaServed': {
            '@type': 'City',
            'name': 'Chicago'
          },
          'description': 'Rent exclusive filming locations in Chicago including studios, rooftops, warehouses, and unique production spaces.'
        }}
      />
      <PublicNavbar />

      <div className="page-header">
        <div className="container">
          <h1 className="page-title">Premium Locations</h1>
          <p className="page-subtitle">
            Discover exclusive filming locations for your production
          </p>
        </div>
      </div>

      <div className="page-content">
        <div className="container">
          {/* Filters */}
          <div className="filters-bar">
            <div className="search-box">
              <Search size={20} />
              <input
                type="text"
                placeholder="Search locations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>

            <div className="sort-box">
              <Filter size={20} />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="sort-select"
              >
                <option value="name">Sort by Name</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
              </select>
            </div>
          </div>

          {/* Results */}
          {loading ? (
            <LoadingSpinner />
          ) : filteredLocations.length === 0 ? (
            <div className="empty-state">
              <h3 className="empty-title">No locations found</h3>
              <p className="empty-text">
                {searchTerm
                  ? 'Try adjusting your search criteria'
                  : 'Check back soon for new locations'}
              </p>
            </div>
          ) : (
            <>
              <div className="results-count">
                Showing {filteredLocations.length} location{filteredLocations.length !== 1 ? 's' : ''}
              </div>
              <div className="locations-grid">
                {filteredLocations.map((location) => (
                  <LocationCard key={location.id} location={location} />
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <PublicFooter />
    </div>
  );
};

export default Locations;